// MessageComponents.jsx - Create this file in your components directory
import React, { useState } from 'react';

// GoLink Component - for displaying clickable go links
export const GoLink = ({ name, description }) => {
  return (
    <button
      onClick={() => window.open(`https://go.middlebury.edu/${name}`, '_blank')}
      className="bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-800/50
                text-blue-700 dark:text-blue-300 pl-1.5 pr-3 py-1.5 rounded-md text-sm font-medium
                transition-colors duration-150 flex items-center shadow-sm"
    >
      <span className="mr-2 text-xs bg-blue-600 text-white px-1.5 py-0.5 rounded">go</span>
      {name}
    </button>
  );
};

// Section Header Component
export const SectionHeader = ({ title, icon }) => {
  return (
    <div className="flex items-center space-x-2 mb-3 pb-1 border-b dark:border-gray-700">
      {icon && <span className="text-blue-500">{icon}</span>}
      <h3 className="font-medium text-gray-900 dark:text-white text-lg">{title}</h3>
    </div>
  );
};

// Collapsible Section Component
export const CollapsibleSection = ({ title, children, icon, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border dark:border-gray-700 rounded-lg overflow-hidden mb-3">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
      >
        <div className="flex items-center space-x-2">
          {icon && <span className="text-blue-500">{icon}</span>}
          <span className="font-medium text-gray-900 dark:text-white">{title}</span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="p-3 bg-white dark:bg-gray-900">
          {children}
        </div>
      )}
    </div>
  );
};

// Tabbed Content Component
export const TabbedContent = ({ tabs }) => {
  const [activeTab, setActiveTab] = useState(0);

  // Add a check to ensure tabs is an array
  if (!tabs || !Array.isArray(tabs) || tabs.length === 0) {
    return <div className="p-4 text-gray-600 dark:text-gray-400">No tab content available</div>;
  }

  return (
    <div className="border dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="flex border-b dark:border-gray-700">
        {tabs.map((tab, index) => (
          <button
            key={index}
            onClick={() => setActiveTab(index)}
            className={`px-4 py-2 text-sm font-medium transition-colors
                      ${activeTab === index 
                        ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400' 
                        : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
          >
            {tab.title}
          </button>
        ))}
      </div>
      <div className="p-4 bg-white dark:bg-gray-900">
        {tabs[activeTab] && tabs[activeTab].content}
      </div>
    </div>
  );
};

// Location Card Component
export const LocationCard = ({ name, address, hours, description }) => {
  return (
    <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div className="p-4">
        <h3 className="font-medium text-lg text-gray-900 dark:text-white mb-1">{name}</h3>
        {address && (
          <div className="flex items-start space-x-2 text-gray-600 dark:text-gray-300 text-sm mb-2">
            <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>{address}</span>
          </div>
        )}
        {hours && (
          <div className="flex items-start space-x-2 text-gray-600 dark:text-gray-300 text-sm mb-2">
            <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{hours}</span>
          </div>
        )}
        {description && (
          <p className="text-gray-700 dark:text-gray-300 text-sm">{description}</p>
        )}
      </div>
      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex justify-end">
        <button
          onClick={() => window.open(`https://go.middlebury.edu/map?${encodeURIComponent(name)}`, '_blank')}
          className="text-blue-600 dark:text-blue-400 text-sm font-medium flex items-center"
        >
          View on Map
          <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Event Card Component
export const EventCard = ({ title, date, time, location, description }) => {
  return (
    <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div className="p-4">
        <h3 className="font-medium text-lg text-gray-900 dark:text-white mb-1">{title}</h3>
        <div className="flex items-start space-x-2 text-gray-600 dark:text-gray-300 text-sm mb-2">
          <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>{date}</span>
        </div>
        {time && (
          <div className="flex items-start space-x-2 text-gray-600 dark:text-gray-300 text-sm mb-2">
            <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{time}</span>
          </div>
        )}
        {location && (
          <div className="flex items-start space-x-2 text-gray-600 dark:text-gray-300 text-sm mb-2">
            <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>{location}</span>
          </div>
        )}
        {description && (
          <p className="text-gray-700 dark:text-gray-300 text-sm">{description}</p>
        )}
      </div>
      <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex justify-end space-x-4">
        <button
          onClick={() => window.open(`https://go.middlebury.edu/events`, '_blank')}
          className="text-blue-600 dark:text-blue-400 text-sm font-medium flex items-center"
        >
          Add to Calendar
          <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Menu Item Component
export const MenuItem = ({ item, isSpecial = false }) => {
  return (
    <div className={`
      flex items-center space-x-2 py-1.5 px-2 rounded-md 
      ${isSpecial ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900' : ''}
    `}>
      <span className={`${isSpecial ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'} mt-0.5`}>•</span>
      <span className="text-gray-800 dark:text-gray-200">{item}</span>
      {isSpecial && (
        <span className="ml-auto bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-xs px-2 py-0.5 rounded-full">
          Special
        </span>
      )}
    </div>
  );
};

// Menu Section Component
export const MenuSection = ({ title, items, mealType }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Determine if this meal type is current based on time of day
  const isCurrentMeal = () => {
    const hour = new Date().getHours();
    if (mealType === 'breakfast' && hour >= 7 && hour < 11) return true;
    if (mealType === 'lunch' && hour >= 11 && hour < 16) return true;
    if (mealType === 'dinner' && (hour >= 16 || hour < 7)) return true;
    return false;
  };

  const isCurrent = isCurrentMeal();

  // Add a check to ensure items is an array
  const safeItems = Array.isArray(items) ? items : [];

  return (
    <div className={`rounded-lg border overflow-hidden transition-shadow
                    ${isCurrent 
                      ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10' 
                      : 'border-gray-200 dark:border-gray-700'}`}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full text-left px-4 py-3 flex justify-between items-center
                  ${isCurrent 
                    ? 'bg-blue-100/80 dark:bg-blue-900/30' 
                    : 'bg-gray-50 dark:bg-gray-800'}`}
      >
        <div className="flex items-center">
          <h3 className={`font-medium ${isCurrent ? 'text-blue-800 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>
            {title}
          </h3>
          {isCurrent && (
            <span className="ml-2 bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
              Current
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 ${isCurrent ? 'text-blue-500' : 'text-gray-400'} transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="p-3 space-y-0.5 bg-white dark:bg-gray-900">
          {safeItems.map((item, index) => (
            <MenuItem
              key={index}
              item={item}
              isSpecial={typeof item === 'string' && item.toLowerCase().includes('special')}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Schedule Item Component
export const ScheduleItem = ({ time, description }) => {
  return (
    <div className="flex items-start space-x-3 py-2">
      <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2.5 py-1 rounded text-sm font-medium w-20 text-center flex-shrink-0">
        {time}
      </div>
      <div className="text-gray-700 dark:text-gray-300 pt-1">
        {description}
      </div>
    </div>
  );
};

// Steps Component
export const Steps = ({ steps }) => {
  // Add a check to ensure steps is an array
  const safeSteps = Array.isArray(steps) ? steps :
                   (steps && typeof steps === 'object' && Array.isArray(steps.steps)) ? steps.steps : [];

  return (
    <div className="space-y-3">
      {safeSteps.map((step, index) => {
        // Handle if step is an object with a text property
        const stepText = typeof step === 'object' && step !== null ? (step.text || '') : step;

        return (
          <div key={index} className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-700 dark:text-blue-300 font-medium">
              {index + 1}
            </div>
            <div className="flex-1 pt-0.5">
              <div className="text-gray-800 dark:text-gray-200">{stepText}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Resource Link Component
export const ResourceLink = ({ title, url, description, icon }) => {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
    >
      <div className="flex items-start space-x-3">
        <div className="mt-0.5 flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-blue-700 dark:text-blue-400">
          {icon || (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {title}
          </h4>
          {description && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{description}</p>
          )}
        </div>
        <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
        </svg>
      </div>
    </a>
  );
};